/**
 * SampleEditor — Right sidebar editor for a single sample
 *
 * Tabs: Details, Settings
 * Follows OnMove creator‑editor pattern.
 */

import { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Autocomplete,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
} from "@mui/material";
import KDBox from "components/KDBox";
import KDTypography from "components/KDTypography";
import KDInput from "components/KDInput";
import KDAlert from "components/KDAlert";
import DetailPageTemplate from "templates/DetailPageTemplate";
import { useT } from "context/I18nContext";
import { useAbility, ACTIONS, SUBJECTS } from "context/AbilityContext";
import useSamples from "hooks/useSamples";
import logger from "services/logger";

const STATUS_OPTIONS = ["draft", "published", "archived"];
const PRIORITY_OPTIONS = ["low", "medium", "high"];
const CATEGORY_OPTIONS = ["General", "Tutorial", "Reference", "Demo"];

function SampleEditor({ sampleId, isNew, onClose, onSaved, onDeleted }) {
  const t = useT();
  const ability = useAbility();
  const { getSample, createSample, updateSample, deleteSample } = useSamples();

  const [form, setForm] = useState({
    title: "",
    description: "",
    category: "",
    status: "draft",
    priority: "medium",
    tags: [],
    isPublic: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const canEdit = isNew
    ? ability.can(ACTIONS.CREATE, SUBJECTS.SAMPLE)
    : ability.can(ACTIONS.UPDATE, SUBJECTS.SAMPLE);
  const canDelete = !isNew && ability.can(ACTIONS.DELETE, SUBJECTS.SAMPLE);

  // Fetch existing sample
  const loadSample = useCallback(async () => {
    if (isNew || !sampleId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getSample(sampleId);
      if (data) {
        setForm({
          title: data.title || "",
          description: data.description || "",
          category: data.category || "",
          status: data.status || "draft",
          priority: data.priority || "medium",
          tags: data.tags || [],
          isPublic: data.isPublic || false,
        });
      }
    } catch (err) {
      logger.error("[SampleEditor]", "Failed to load:", err.message);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [sampleId, isNew, getSample]);

  useEffect(() => {
    loadSample();
  }, [loadSample]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setError(null);
    if (!form.title.trim()) {
      setError(t("editor.requiredField", { field: t("samples.title_label") }));
      return;
    }
    try {
      if (isNew) {
        await createSample(form);
      } else {
        await updateSample(sampleId, form);
      }
      onSaved?.();
      onClose();
    } catch (err) {
      logger.error("[SampleEditor]", "Save failed:", err.message);
      setError(err.message);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteSample(sampleId);
      onDeleted?.();
      onClose();
    } catch (err) {
      logger.error("[SampleEditor]", "Delete failed:", err.message);
      setError(err.message);
    }
  };

  // ── Tab 1: Details ────────────────────────────
  const detailsTab = (
    <KDBox display="flex" flexDirection="column" gap={2.5}>
      {error && (
        <KDAlert color="error" dismissible onClose={() => setError(null)}>
          {error}
        </KDAlert>
      )}

      {/* Title */}
      <KDBox>
        <KDTypography variant="subtitle2" fontWeight="medium" mb={0.5}>
          {t("samples.title_label")} *
        </KDTypography>
        <KDInput
          value={form.title}
          onChange={(e) => handleChange("title", e.target.value)}
          disabled={!canEdit}
          placeholder={t("samples.title_label")}
        />
      </KDBox>

      {/* Description */}
      <KDBox>
        <KDTypography variant="subtitle2" fontWeight="medium" mb={0.5}>
          {t("samples.description_label")}
        </KDTypography>
        <KDInput
          value={form.description}
          onChange={(e) => handleChange("description", e.target.value)}
          disabled={!canEdit}
          multiline
          rows={4}
          placeholder={t("samples.description_label")}
        />
      </KDBox>

      {/* Category */}
      <KDBox>
        <KDTypography variant="subtitle2" fontWeight="medium" mb={0.5}>
          {t("samples.category")}
        </KDTypography>
        <Autocomplete
          value={form.category || null}
          options={CATEGORY_OPTIONS}
          onChange={(_e, val) => handleChange("category", val || "")}
          disabled={!canEdit}
          renderInput={(params) => (
            <TextField {...params} size="small" placeholder={t("samples.category")} />
          )}
        />
      </KDBox>

      {/* Tags */}
      <KDBox>
        <KDTypography variant="subtitle2" fontWeight="medium" mb={0.5}>
          {t("samples.tags")}
        </KDTypography>
        <Autocomplete
          multiple
          freeSolo
          value={form.tags}
          options={[]}
          onChange={(_e, val) => handleChange("tags", val)}
          disabled={!canEdit}
          renderTags={(value, getTagProps) =>
            value.map((tag, index) => (
              <Chip
                label={tag}
                size="small"
                {...getTagProps({ index })}
                key={tag}
              />
            ))
          }
          renderInput={(params) => (
            <TextField {...params} size="small" placeholder={t("samples.addTag")} />
          )}
        />
      </KDBox>
    </KDBox>
  );

  // ── Tab 2: Settings ───────────────────────────
  const settingsTab = (
    <KDBox display="flex" flexDirection="column" gap={2.5}>
      {/* Status */}
      <KDBox>
        <KDTypography variant="subtitle2" fontWeight="medium" mb={0.5}>
          {t("samples.status")}
        </KDTypography>
        <Autocomplete
          value={form.status}
          options={STATUS_OPTIONS}
          getOptionLabel={(opt) => t(`samples.status_${opt}`) || opt}
          onChange={(_e, val) => handleChange("status", val || "draft")}
          disabled={!canEdit}
          disableClearable
          renderInput={(params) => (
            <TextField {...params} size="small" />
          )}
        />
      </KDBox>

      {/* Priority */}
      <KDBox>
        <KDTypography variant="subtitle2" fontWeight="medium" mb={0.5}>
          {t("samples.priority")}
        </KDTypography>
        <Autocomplete
          value={form.priority}
          options={PRIORITY_OPTIONS}
          getOptionLabel={(opt) => t(`samples.priority_${opt}`) || opt}
          onChange={(_e, val) => handleChange("priority", val || "medium")}
          disabled={!canEdit}
          disableClearable
          renderInput={(params) => (
            <TextField {...params} size="small" />
          )}
        />
      </KDBox>

      {/* Public toggle */}
      <KDBox>
        <FormControlLabel
          control={
            <Switch
              checked={form.isPublic}
              onChange={(e) => handleChange("isPublic", e.target.checked)}
              disabled={!canEdit}
            />
          }
          label={
            <KDTypography variant="subtitle2" fontWeight="medium">
              {t("samples.isPublic")}
            </KDTypography>
          }
        />
      </KDBox>
    </KDBox>
  );

  return (
    <DetailPageTemplate
      open
      onClose={onClose}
      title={isNew ? t("samples.create") : t("samples.editTitle")}
      loading={loading}
      tabs={[
        { label: t("editor.details"), content: detailsTab },
        { label: t("editor.settings"), content: settingsTab },
      ]}
      onSave={canEdit ? handleSave : null}
      onDelete={canDelete ? handleDelete : null}
      saveLabel={isNew ? t("common.create") : t("common.save")}
      deleteLabel={t("common.delete")}
    />
  );
}

SampleEditor.propTypes = {
  sampleId: PropTypes.string,
  isNew: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
  onDeleted: PropTypes.func,
};

SampleEditor.defaultProps = {
  sampleId: null,
  isNew: false,
  onSaved: null,
  onDeleted: null,
};

export default SampleEditor;
